// The fetch layer. Knows about tokens and status codes; knows nothing about React.
//
// The three failure modes are separate classes rather than one error with a status field,
// because the page does something genuinely different with each and the old code's comment
// about that is worth keeping honest:
//
// * **401** -- the session died under us. Back to the gate; signing in again fixes it.
// * **403** -- the session is fine and this caller may not do this particular thing. Two
//   different refusals share that status and the *body* is what tells them apart: FastAPI's
//   dependency refuses the **caller** with `{"detail": ...}`, which is no access at all and
//   replaces the page, while an Amplemarket endpoint refuses one **action** with
//   `{"ok": false, "error": {...}}` whose reason names the account's owner. The second has to
//   reach the panel that asked, as an ordinary `ApiError`, with the page still under it.
// * anything else -- a real failure, rendered where it happened.

import type {
  ActivityPayload, ActivityThreadPayload, AdminBriefsPayload, AdminOverviewPayload, AdminRolesPayload,
  ApprovalQueuePayload, AuthConfig, BookPayload, BriefPayload, ChatReply, ChatThreadKey,
  ChatConversationsReply, ChatEventsReply, ChatHistoryReply,
  IntakeBatchReply, IntakeEventsPayload, IntakeRowRequest, JobRunsPayload,
  FundingPayload, NotificationsPayload, Sequence, SlackLinkRequest, SlackLinkResult,
  LeadList, PushResult, EnrichCandidate, StrategistOption, ViewAsPerson,
  WriteActionResult, MyRequestsPayload, MeResponse, RouteRequestBody,
  AutoSendContestPayload, AutoSendCount, AutoSendQueuePayload, ContactEditBody, RetireApplyPayload, RetirePayload,
  StyleIntakeRequest, StyleRulesRequest, StyleState,
  ExtensionPersonAnswer, ExtensionRecord, TurnViewBody,
  SequenceConnection, SequenceCreateReply, SequenceDraft, SequenceLaunchReply, SequenceMailboxes, SequencePersonalLineDraft, SequenceStep,
  SequenceStepDraft, Q4UploadReceipt, Q4UploadStatus, ScoreDetailPayload, ScoreHistoryPayload, ScoresPayload,
  CompetitorOverridePayload,
} from './types';
import { mergeBookDelta, type BookDelta } from './bookDelta.ts';

// The one header that changes whose book a request reads. A header, never a query
// parameter — it must not land in a URL. sessionStorage so a closed tab forgets it.
export const VIEW_AS_HEADER = 'X-Scout-View-As';
export const VIEW_AS_STORAGE_KEY = 'scout-view-as';

// The extension's framed pages (`/embed/*`) never view as anyone: the extension is exempt from
// view-as server-side (`_request_ignores_view_as`), and its chats must be the signed-in person's.
export const EMBED_PATH_PREFIX = '/embed/';

export function isEmbedPath(pathname: string | undefined | null): boolean {
  return typeof pathname === 'string' && pathname.startsWith(EMBED_PATH_PREFIX);
}

export function readViewAs(): string | null {
  if (isEmbedPath(globalThis.location?.pathname)) return null;
  try {
    const storage = globalThis.sessionStorage;
    if (!storage) return null;
    const value = storage.getItem(VIEW_AS_STORAGE_KEY);
    return value && value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
}

// Admin may borrow any weaker role and leadership an AE (GRO-360); anybody already viewing as
// somebody keeps the control, because their resolved role is the borrowed one.
export function viewAsSwitcherVisible(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'leadership' || Boolean(readViewAs());
}

export function writeViewAs(email: string | null): void {
  try {
    const storage = globalThis.sessionStorage;
    if (!storage) return;
    if (email && email.trim()) storage.setItem(VIEW_AS_STORAGE_KEY, email.trim());
    else storage.removeItem(VIEW_AS_STORAGE_KEY);
  } catch {
    // Private mode. The switcher still works for this tab until reload.
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super('Your session expired. Sign in again to continue.');
    this.name = 'SessionExpiredError';
  }
}

export class AccessDeniedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AccessDeniedError';
  }
}

export class ApiError extends Error {
  status: number;
  // The server's own refusal code (`preview`, `withheld`, ...), when it sent one.
  code: string | null;
  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// How a thrown error reads in a panel that is staying on screen.
//
// This is here rather than beside the panels because it is the fourth thing the classes above
// decide, and it decides it from the status: a refusal is not a failure and must not be dressed
// as one. The server frames the scoped refusals itself -- "Not pushed - that account is owned
// by ..." -- so a prefix in front of one says it twice. A 5xx, a dead session, or a
// caller-level refusal is a genuine failure of what the panel was asked to do, and needs the
// sentence.
export function panelMessage(error: unknown, prefix: string): string {
  const message = (error as Error).message;
  if (error instanceof ApiError && error.status < 500) return message;
  return prefix + ' ' + message;
}

// On a PR preview the server refuses every Amplemarket push on purpose (code `preview`). That is
// the dry run a preview is for, not a failure, so a panel says so neutrally and never "failed".
export const PREVIEW_DRY_RUN_MESSAGE = 'Dry run — preview deployment, nothing was pushed.';

export function isPreviewDryRun(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'preview';
}

// Clerk session tokens are short lived and Clerk refreshes them, so this asks for one per
// request rather than holding a copy that goes stale. `skipCache` asks for a fresh one after a
// 401: Clerk's own `getToken` honours it, and so does the extension frame's token source.
export interface TokenOptions {
  skipCache?: boolean;
}
export type TokenGetter = (options?: TokenOptions) => Promise<string | null>;

function withToken(headers: Record<string, string>, token: string | null): Record<string, string> {
  return token ? { ...headers, Authorization: 'Bearer ' + token } : headers;
}

// One fetch with the session token, retried once with a fresh token when the server refuses
// the first. A 401 means the server did nothing, so a retry cannot double a write.
async function fetchWithToken(path: string, getToken: TokenGetter, init: RequestInit & { headers: Record<string, string> }): Promise<Response> {
  const token = await getToken();
  const response = await fetch(path, { ...init, headers: withToken(init.headers, token) });
  if (response.status !== 401 || !token) return response;

  const freshToken = await getToken({ skipCache: true });
  if (!freshToken || freshToken === token) return response;
  return fetch(path, { ...init, headers: withToken(init.headers, freshToken) });
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  // Lets the request outlive the page: a save sent while the tab unloads.
  keepalive?: boolean;
}

// Every failure body this server produces, in one shape.
//
// FastAPI's own refusals carry `detail`. The Amplemarket endpoints carry `ok: false` and an
// `error` **object** -- reading that object as a message is what put "[object Object]" in front
// of people. The book endpoint carries `error` as a plain **string** when a build failed. All
// three are read in one place so no call site has to know which shape it got.
interface FailureBody {
  ok?: boolean;
  detail?: string;
  error?: string | { code?: string | null; message?: string };
}

function failureCode(body: FailureBody): string | null {
  return body.error && typeof body.error === 'object' ? body.error.code ?? null : null;
}

function failureMessage(body: FailureBody, fallback: string): string {
  if (typeof body.error === 'string') return body.error || fallback;
  if (body.error && body.error.message) return body.error.message;
  return body.detail || fallback;
}

async function request<T>(
  path: string,
  getToken: TokenGetter,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  const viewAs = readViewAs();
  if (viewAs) headers[VIEW_AS_HEADER] = viewAs;

  const response = await fetchWithToken(path, getToken, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    ...(options.keepalive ? { keepalive: true } : {}),
  });

  if (response.status === 401) throw new SessionExpiredError();

  if (!response.ok) {
    const failure = (await response.json().catch(() => ({}))) as FailureBody;

    // Which of the two 403s this is. `ok: false` marks the scoped one; anything else -- including
    // a body that would not parse -- falls to the caller-level refusal, which is the safe way
    // round, because a refusal nobody can read still has to read as a refusal.
    if (response.status === 403 && failure.ok !== false) {
      throw new AccessDeniedError(failure.detail || 'Ask Vikram to be given access.');
    }
    throw new ApiError(failureMessage(failure, response.statusText), response.status, failureCode(failure));
  }

  const payload = await response.json();

  // Two different in-band failure shapes, both answered with a 200.
  //
  // The book endpoint carries `error` as a *string* when the build itself failed, so a broken
  // Snowflake read surfaces as a message rather than as an empty page. The
  // Amplemarket endpoints carry `ok: false` and an `error` *object*, because a refused push is
  // an ordinary answer about the write rather than a transport failure.
  if (payload && typeof payload === 'object') {
    const body = payload as FailureBody;
    if (body.ok === false || (typeof body.error === 'string' && body.error)) {
      throw new ApiError(failureMessage(body, 'The request failed.'), response.status, failureCode(body));
    }
  }
  return payload as T;
}

// Open by design, and safe to be: a publishable key is meant to ship to browsers, and this is
// what an anonymous visitor needs in order to sign in at all.
export async function fetchAuthConfig(): Promise<AuthConfig> {
  const response = await fetch('/api/auth/config');
  if (!response.ok) {
    throw new Error('The server did not return its Clerk configuration.');
  }
  return response.json();
}

// Forcing a rebuild is admin-only, because it spends about eleven seconds of Snowflake on
// everybody's behalf. For everyone else `refresh` re-fetches the cached payload instead of the
// button disappearing: from where they sit it means "get me current data", and since the
// scheduler landed the cache *is* current.
//
// Two requests, merged (bookDelta.ts): the delta first (`?delta=1`; without it the server
// answers 409 "reload", for a tab still running the previous build), because it names the base
// version it was cut from, then that base. The base is the same bytes until the next rebuild,
// so the browser revalidates it to a 304.
//
// The base can miss for a moment: a 409 means a rebuild landed between the two requests, and a
// 404 is a deploy rolling over. A first 409 refetches the pair at once; after that, a 409 or a
// 404 waits `bookRetry.delayMilliseconds` and tries the pair one last time, rather than putting
// an error on the page. Retries are plain reads: a forced rebuild is never repeated.
export const bookRetry = { delayMilliseconds: 1000 };

function isBaseMiss(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 409 || error.status === 404);
}

function waitMilliseconds(milliseconds: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}

export async function fetchBook(getToken: TokenGetter, forceRebuild = false): Promise<BookPayload> {
  for (let attempt = 0; ; attempt += 1) {
    const delta = forceRebuild && attempt === 0
      ? await request<BookDelta>('/api/book/refresh?delta=1', getToken, { method: 'POST' })
      : await request<BookDelta>('/api/book?delta=1', getToken);
    const basePath = '/api/book/base?version=' + encodeURIComponent(delta.base_version ?? '');
    try {
      const base = await request<BookPayload>(basePath, getToken);
      return mergeBookDelta(base, delta);
    } catch (error) {
      if (attempt >= 2 || !isBaseMiss(error)) throw error;

      // A first rebuild race retries at once; anything after that waits a moment first.
      const immediate = attempt === 0 && error instanceof ApiError && error.status === 409;
      if (!immediate) await waitMilliseconds(bookRetry.delayMilliseconds);
    }
  }
}


// Who the caller is and which board to land them on, with no account data attached. The
// landing redirect uses this rather than the book: deciding that Deborah belongs on the
// activity view is a lookup, and making her wait for 694KB of accounts she is about to
// navigate away from is not.
export function fetchMe(getToken: TokenGetter): Promise<MeResponse> {
  return request<MeResponse>('/api/me', getToken);
}

// An AE's own accounts (GRO-360). One plain read: the server has already cut the book down to the
// caller's Salesforce-owned rows, so there is no base-plus-delta to merge. The payload is book
// shaped (empty `owners` and `coverage`, which this view does not read) so the sidebar takes it.
export function fetchAeBook(getToken: TokenGetter): Promise<BookPayload> {
  return request<BookPayload>('/api/ae/book', getToken);
}

export function fetchViewAsPeople(
  getToken: TokenGetter,
): Promise<{ people: ViewAsPerson[] }> {
  return request<{ people: ViewAsPerson[] }>('/api/admin/strategists', getToken);
}

// The funnel, the per-strategist table and the trend, for one window. Separate from the book
// because it is a different question over a different query -- and because it takes a
// parameter the book does not have.
export function fetchActivity(
  getToken: TokenGetter,
  window: string,
): Promise<ActivityPayload> {
  return request<ActivityPayload>(`/api/activity?window=${encodeURIComponent(window)}`, getToken);
}

// Lazy: one account, on click. Not part of the activity payload — that stream is
// day-grain counts and cannot render a thread.
export function fetchActivityThread(
  getToken: TokenGetter,
  accountId: string,
): Promise<ActivityThreadPayload> {
  return request<ActivityThreadPayload>(
    `/api/activity/accounts/${encodeURIComponent(accountId)}/thread`,
    getToken,
  );
}


// The picker searches rather than lists, because the workspace holds over 1,500 sequences --
// most of them generated one per prospect. Filtering to active and draft happens on the server;
// a paused sequence never reaches this page. `truncated` says a capped search is not an answer.
export function fetchSequences(
  getToken: TokenGetter,
  query: string,
): Promise<{ sequences: Sequence[]; truncated?: boolean }> {
  return request(`/api/amplemarket/sequences?query=${encodeURIComponent(query)}`, getToken);
}

export function fetchLeadLists(
  getToken: TokenGetter,
  query: string,
): Promise<{ lead_lists: LeadList[]; truncated?: boolean }> {
  return request(`/api/amplemarket/lead-lists?query=${encodeURIComponent(query)}`, getToken);
}

// The browser never sends an email address. It names an account and the contacts on it, and the
// server reads the addresses out of the book payload it already built -- or out of what an
// enrichment found and somebody accepted, which it also holds. A push can only reach somebody
// this dashboard is already showing.
export interface PushRequestBody {
  target_type: 'sequence' | 'lead_list';
  target_id: string;
  // Only used server-side to find the target again and re-check that it still accepts leads.
  // The name that comes back is Amplemarket's, not this one.
  target_name: string;
  account_id: string;
  contact_ids: string[];
}

export function pushToAmplemarket(
  getToken: TokenGetter,
  body: PushRequestBody,
): Promise<PushResult> {
  return request('/api/amplemarket/push', getToken, { method: 'POST', body });
}

// Named for what it finds rather than for an address, because it is not always an address: a
// contact with no email is looked up for one, and a contact with an email and no name is looked
// up for the name that 45 of the 80 sampled sequences reference. Either way the body names the
// contact and nothing else -- the server resolves what is found and holds it.
export function findContactDetails(
  getToken: TokenGetter,
  body: { account_id: string; contact_id: string },
): Promise<{ found: boolean; candidate: EnrichCandidate | null }> {
  return request('/api/amplemarket/enrich', getToken, { method: 'POST', body });
}

// The second half of the two-step. Nothing found by a lookup is usable by a push until somebody
// has seen the candidate and said yes -- a wrong match does not just store a bad row, it puts a
// stranger into a live email sequence under a colleague's name.
export function acceptContactDetails(
  getToken: TokenGetter,
  body: { account_id: string; contact_id: string },
): Promise<{ ok: boolean }> {
  return request('/api/amplemarket/enrich/accept', getToken, { method: 'POST', body });
}

export function fetchFunding(getToken: TokenGetter): Promise<FundingPayload> {
  return request<FundingPayload>('/api/book/funding', getToken);
}

export function fetchScores(getToken: TokenGetter): Promise<ScoresPayload> {
  return request<ScoresPayload>('/api/scores', getToken);
}

export function fetchScoreDetail(getToken: TokenGetter, accountId: string): Promise<ScoreDetailPayload> {
  return request<ScoreDetailPayload>(`/api/scores/${encodeURIComponent(accountId)}`, getToken);
}

// The sidebar's "Not a competitor" toggle (GRO-425).
export function fetchCompetitorOverride(getToken: TokenGetter, accountId: string): Promise<CompetitorOverridePayload> {
  return request<CompetitorOverridePayload>(`/api/competitor-override/${encodeURIComponent(accountId)}`, getToken);
}

export function postCompetitorOverride(
  getToken: TokenGetter,
  accountId: string,
  body: { not_competitor: boolean; reason?: string },
): Promise<CompetitorOverridePayload> {
  return request<CompetitorOverridePayload>(`/api/competitor-override/${encodeURIComponent(accountId)}`, getToken,
    { method: 'POST', body });
}

export function fetchScoreHistory(getToken: TokenGetter, accountId: string, days = 90): Promise<ScoreHistoryPayload> {
  return request<ScoreHistoryPayload>(`/api/scores/${encodeURIComponent(accountId)}/history?days=${days}`, getToken);
}

export function fetchBriefToday(getToken: TokenGetter): Promise<BriefPayload> {
  return request<BriefPayload>('/api/briefs/today', getToken);
}

export function fetchNotifications(getToken: TokenGetter): Promise<NotificationsPayload> {
  return request<NotificationsPayload>('/api/notifications', getToken);
}

export function markNotificationsRead(
  getToken: TokenGetter,
  notificationIds?: number[],
): Promise<{ marked: number }> {
  const body = notificationIds === undefined
    ? {}
    : { notification_ids: notificationIds };
  return request('/api/notifications/read', getToken, { method: 'POST', body });
}

export function postIntake(
  getToken: TokenGetter,
  body: IntakeRowRequest,
): Promise<IntakeBatchReply> {
  return request('/api/intake', getToken, { method: 'POST', body });
}

export function claimAccount(
  getToken: TokenGetter,
  body: {
    domain: string;
    // "Why this account?" — required; the server answers 400 without it.
    reason: string;
    evidence?: string;
    owner?: string;
    name?: string;
    account_id?: string;
    // False for an upload's rows: the upload announces them in Slack together at the end.
    announce?: boolean;
  },
): Promise<WriteActionResult> {
  return request('/api/accounts/claim', getToken, { method: 'POST', body });
}

// One Slack message for an upload's requests (`board/request_cards.py`). Fire and forget: the
// requests are saved already, and the server answers before Slack is reached.
export function announceRequests(
  getToken: TokenGetter,
  requests: { account_id: string; owner: string }[],
): Promise<{ accepted: boolean; request_count: number }> {
  return request('/api/accounts/requests/announce', getToken, { method: 'POST', body: { requests } });
}

export function assignAccount(
  getToken: TokenGetter,
  body: {
    domain: string;
    owner: string;
    reason?: string;
    account_id?: string;
    // The CSV upload's "Assign anyway" over the person gate's forceable reasons.
    override_exclusion?: boolean;
  },
): Promise<WriteActionResult> {
  return request('/api/accounts/assign', getToken, { method: 'POST', body });
}

// An AE asks for an available account to go to a listed strategist (go-live). Queues like a claim;
// an approver decides. Answers `requested`, or `refused` with a reason.
export function requestRoute(
  getToken: TokenGetter,
  body: RouteRequestBody,
): Promise<WriteActionResult> {
  return request('/api/accounts/request-route', getToken, { method: 'POST', body });
}

// `account_id` is the row's Account: on a duplicate domain the caller may own the record the
// domain lookup would not prefer. Pass it whenever the row has one.
export function returnAccount(
  getToken: TokenGetter,
  body: { domain: string; reason: string; account_id?: string },
): Promise<WriteActionResult> {
  return request('/api/accounts/return', getToken, { method: 'POST', body });
}

export function snoozeAccount(
  getToken: TokenGetter,
  body: { domain: string; until: string; account_id?: string },
): Promise<WriteActionResult> {
  return request('/api/accounts/snooze', getToken, { method: 'POST', body });
}

export function setBoardPref(
  getToken: TokenGetter,
  body: { account_id: string; preference: 'pinned' | 'hidden' | null },
): Promise<{ account_id: string; preference: 'pinned' | 'hidden' | null }> {
  return request('/api/accounts/board-pref', getToken, { method: 'POST', body });
}

// Flag or unflag an account High priority. Admin and leadership: any account; a strategist: their own.
export interface PriorityFlagResult {
  account_id: string;
  high_priority: boolean;
  high_priority_set_by: string | null;
  high_priority_set_at: string | null;
}

export function setAccountPriority(
  getToken: TokenGetter,
  body: { account_id: string; high_priority: boolean },
): Promise<PriorityFlagResult> {
  return request('/api/accounts/priority', getToken, { method: 'POST', body });
}

// The owner picker's list. The queue carries it too, but only an approver may read the queue.
export function fetchStrategists(
  getToken: TokenGetter,
): Promise<{ strategists: StrategistOption[] }> {
  return request<{ strategists: StrategistOption[] }>('/api/strategists', getToken);
}

export function fetchMyRequests(getToken: TokenGetter): Promise<MyRequestsPayload> {
  return request<MyRequestsPayload>('/api/requests/mine', getToken);
}

export function fetchApprovalQueue(getToken: TokenGetter): Promise<ApprovalQueuePayload> {
  return request<ApprovalQueuePayload>('/api/approval-queue', getToken);
}

export function approveQueue(
  getToken: TokenGetter,
  body: {
    account_ids?: string[];
    owners?: Record<string, string>;
    requesters?: Record<string, string>;
    // "Approve anyway" over the person gate's forceable reasons (GRO-161 round 1).
    override_exclusion?: boolean;
  } = {},
): Promise<ApprovalQueuePayload> {
  return request('/api/approval-queue/approve', getToken, { method: 'POST', body });
}

// The auto lane's review queue (W9). Every write is a POST and binds the real actor.
export function fetchAutoSendQueue(getToken: TokenGetter): Promise<AutoSendQueuePayload> {
  return request<AutoSendQueuePayload>('/api/auto-send/queue', getToken);
}

export function fetchAutoSendCount(getToken: TokenGetter): Promise<AutoSendCount> {
  return request<AutoSendCount>('/api/auto-send/count', getToken);
}

// One account's full writers' contests. The queue read leaves the losing writers' emails out.
export function fetchAutoSendContest(getToken: TokenGetter, accountId: string): Promise<AutoSendContestPayload> {
  return request<AutoSendContestPayload>(`/api/auto-send/contest/${encodeURIComponent(accountId)}`, getToken);
}

export function approveAutoSend(
  getToken: TokenGetter,
  accountId: string,
  edits?: ContactEditBody[],
): Promise<AutoSendQueuePayload> {
  return request('/api/auto-send/approve', getToken, {
    method: 'POST', body: { account_id: accountId, edits: edits && edits.length ? edits : undefined },
  });
}

export function approveAutoSendBulk(getToken: TokenGetter, accountIds: string[]): Promise<AutoSendQueuePayload> {
  return request('/api/auto-send/approve-bulk', getToken, { method: 'POST', body: { account_ids: accountIds } });
}

// After checking Amplemarket by hand: the unconfirmed push is there (`sent`) or not (`not_sent`).
export function resolveAutoSend(
  getToken: TokenGetter,
  accountId: string,
  outcome: 'sent' | 'not_sent',
): Promise<AutoSendQueuePayload> {
  return request('/api/auto-send/resolve', getToken, { method: 'POST', body: { account_id: accountId, outcome } });
}

export function rejectAutoSend(
  getToken: TokenGetter,
  accountId: string,
  reason: string,
  note: string,
): Promise<AutoSendQueuePayload> {
  return request('/api/auto-send/reject', getToken, {
    method: 'POST', body: { account_id: accountId, reason, note: note || undefined },
  });
}

export function declineQueue(
  getToken: TokenGetter,
  body: {
    account_ids: string[];
    reason: string;
    requesters?: Record<string, string>;
  },
): Promise<ApprovalQueuePayload> {
  return request('/api/approval-queue/decline', getToken, { method: 'POST', body });
}

export function postChat(
  getToken: TokenGetter,
  body: { message: string; account_id?: string; conversation_id?: string; view?: TurnViewBody },
): Promise<ChatReply> {
  return request('/api/chat', getToken, { method: 'POST', body });
}

// --- the Chrome extension's framed panel (W8) ------------------------------------------------
// Every one of these is a `/api/extension/*` route, which the server answers as the signed-in
// person whatever view-as says.

export function fetchExtensionRecord(getToken: TokenGetter, domain: string): Promise<ExtensionRecord> {
  return request(`/api/extension/record?domain=${encodeURIComponent(domain)}`, getToken);
}

export function postExtensionClaim(
  getToken: TokenGetter,
  body: { domain: string; reason: string; name?: string; owner?: string; account_id?: string; evidence?: string },
): Promise<WriteActionResult> {
  return request('/api/extension/claim', getToken, { method: 'POST', body });
}

export function postExtensionRoute(
  getToken: TokenGetter,
  body: { domain: string; owner: string; account_id?: string },
): Promise<WriteActionResult> {
  return request('/api/extension/route', getToken, { method: 'POST', body });
}

export function postExtensionDraftReply(
  getToken: TokenGetter,
  body: { selection: string; draft?: string; subject?: string; sender?: string },
): Promise<{ available: boolean; draft: string | null; unavailable_reason: string | null }> {
  return request('/api/extension/draft_reply', getToken, { method: 'POST', body });
}

export function postEnrichPerson(
  getToken: TokenGetter,
  body: { linkedin_url: string; name?: string; title?: string; company?: string; domain?: string },
): Promise<ExtensionPersonAnswer> {
  return request('/api/extension/person/enrich', getToken, { method: 'POST', body });
}

export function postPersonPushCard(
  getToken: TokenGetter,
  body: { person_key: string; target_id: string; target_name: string },
): Promise<{ available: boolean; reason?: string | null; approval?: unknown }> {
  return request('/api/extension/person/push_card', getToken, { method: 'POST', body });
}

export function postPersonPush(
  getToken: TokenGetter,
  body: { card_id: string; decision: 'approve' | 'cancel' },
): Promise<{ ok: boolean; outcome?: string; detail?: string | null; reason?: string | null; target_name?: string }> {
  return request('/api/extension/person/push', getToken, { method: 'POST', body });
}

// --- the streamed chat (GRO-274) -------------------------------------------------------------

function threadQuery(thread: ChatThreadKey): URLSearchParams {
  const query = new URLSearchParams();
  if (thread.account_id) query.set('account_id', thread.account_id);
  if (thread.conversation_id) query.set('conversation_id', thread.conversation_id);
  return query;
}

/** The live feed as a raw streaming response: `request` would wait for the whole body. */
export async function openChatStream(getToken: TokenGetter, thread: ChatThreadKey, cursor: number | null, signal: AbortSignal): Promise<Response> {
  const query = threadQuery(thread);
  if (cursor !== null) query.set('cursor', String(cursor));
  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  const viewAs = readViewAs();
  if (viewAs) headers[VIEW_AS_HEADER] = viewAs;
  const response = await fetchWithToken(`/api/chat/stream?${query.toString()}`, getToken, { headers, signal, cache: 'no-store' });
  if (response.status === 401) throw new SessionExpiredError();
  if (!response.ok) throw new ApiError(`The live feed answered ${response.status}.`, response.status);
  // 503 is the server's relay cap: the caller long-polls this read instead.
  return response;
}

/** The long-poll fallback: what the feed sent within `wait` seconds, and where to read next. */
export function fetchChatEvents(getToken: TokenGetter, thread: ChatThreadKey, cursor: number | null, wait: number): Promise<ChatEventsReply> {
  const query = threadQuery(thread);
  if (cursor !== null) query.set('cursor', String(cursor));
  query.set('wait', String(wait));
  return request(`/api/chat/events?${query.toString()}`, getToken);
}

export function postChatStop(getToken: TokenGetter, thread: ChatThreadKey): Promise<{ stopped: boolean; reason: string | null }> {
  return request('/api/chat/stop', getToken, { method: 'POST', body: thread });
}

export function fetchChatHistory(getToken: TokenGetter, thread: ChatThreadKey): Promise<ChatHistoryReply> {
  return request(`/api/chat/history?${threadQuery(thread).toString()}`, getToken);
}

export function fetchChatConversations(getToken: TokenGetter): Promise<ChatConversationsReply> {
  return request('/api/chat/conversations', getToken);
}

export function postChatSeen(getToken: TokenGetter, thread: ChatThreadKey): Promise<{ seen: boolean }> {
  return request('/api/chat/seen', getToken, { method: 'POST', body: thread });
}

// --- the Approve card (GRO-162 §2.2) --------------------------------------------------------

/** The card this conversation waits on, so it survives a reload. */
export function fetchChatPending(getToken: TokenGetter, thread: ChatThreadKey): Promise<ChatReply> {
  const query = new URLSearchParams();
  if (thread.account_id) query.set('account_id', thread.account_id);
  if (thread.conversation_id) query.set('conversation_id', thread.conversation_id);
  return request(`/api/chat/pending?${query.toString()}`, getToken);
}

/** One click for the whole card: every request it showed, one answer. */
export function postChatRespond(
  getToken: TokenGetter,
  thread: ChatThreadKey,
  decision: 'approve' | 'cancel',
  requestIds: string[],
  sequenceChoices: Record<string, string> = {},
): Promise<ChatReply> {
  return request('/api/chat/respond', getToken, {
    method: 'POST',
    body: { ...thread, decision, request_ids: requestIds, sequence_choices: sequenceChoices },
  });
}

/** Re-read a resumed turn that was still running, from where it starts. */
export function postChatResult(getToken: TokenGetter, thread: ChatThreadKey, streamStartIndex: number): Promise<ChatReply> {
  return request('/api/chat/result', getToken, {
    method: 'POST',
    body: { ...thread, stream_start_index: streamStartIndex },
  });
}

export function recordBriefOpen(getToken: TokenGetter): Promise<{ recorded: boolean }> {
  return request('/api/stats/brief-open', getToken, { method: 'POST', body: {} });
}

// --- the admin console ---------------------------------------------------------------------
// Six reads and two writes, all behind `require_admin_view`. Every payload carries
// `unavailable`, which is a *reason* rather than an error: a dead Postgres must not blank the
// one surface that exists to report failure, so these never throw on it.

export function fetchAdminOverview(getToken: TokenGetter): Promise<AdminOverviewPayload> {
  return request<AdminOverviewPayload>('/api/admin/overview', getToken);
}

export interface IntakeEventFilters {
  outcome?: string;
  source?: string;
  reason?: string;
  domain?: string;
  limit?: number;
}

// Built with URLSearchParams rather than by hand: `domain` is whatever somebody typed into a
// search box, and an unescaped `&` in it would silently become a second filter.
export function fetchIntakeEvents(
  getToken: TokenGetter,
  filters: IntakeEventFilters = {},
): Promise<IntakeEventsPayload> {
  const query = new URLSearchParams();
  if (filters.outcome) query.set('outcome', filters.outcome);
  if (filters.source) query.set('source', filters.source);
  if (filters.reason) query.set('reason', filters.reason);
  if (filters.domain) query.set('domain', filters.domain);
  if (filters.limit) query.set('limit', String(filters.limit));
  const suffix = query.toString();
  return request<IntakeEventsPayload>(
    '/api/admin/intake-events' + (suffix ? '?' + suffix : ''),
    getToken,
  );
}

export function fetchJobRuns(
  getToken: TokenGetter,
  jobName?: string,
): Promise<JobRunsPayload> {
  const query = new URLSearchParams();
  if (jobName) query.set('job_name', jobName);
  const suffix = query.toString();
  return request<JobRunsPayload>(
    '/api/admin/job-runs' + (suffix ? '?' + suffix : ''),
    getToken,
  );
}

export function fetchAdminBriefs(
  getToken: TokenGetter,
  briefOn?: string,
): Promise<AdminBriefsPayload> {
  const query = new URLSearchParams();
  if (briefOn) query.set('brief_on', briefOn);
  const suffix = query.toString();
  return request<AdminBriefsPayload>(
    '/api/admin/briefs' + (suffix ? '?' + suffix : ''),
    getToken,
  );
}

export function fetchAdminRoles(getToken: TokenGetter): Promise<AdminRolesPayload> {
  return request<AdminRolesPayload>('/api/admin/roles', getToken);
}

export function grantRole(
  getToken: TokenGetter,
  body: { email: string; role: string; note?: string },
): Promise<AdminRolesPayload> {
  return request('/api/admin/roles/grant', getToken, { method: 'POST', body });
}

// The Q4 targets CSV: staged in Postgres for `jobs/load_q4_targets.py --from-upload`, never
// loaded by the upload itself. The file is read in the browser and sent as text.
export function fetchQ4Upload(getToken: TokenGetter): Promise<Q4UploadStatus> {
  return request<Q4UploadStatus>('/api/admin/q4-targets/upload', getToken);
}

export function uploadQ4Targets(
  getToken: TokenGetter,
  body: { file_name: string; content: string },
): Promise<Q4UploadReceipt> {
  return request<Q4UploadReceipt>('/api/admin/q4-targets/upload', getToken, { method: 'POST', body });
}

// Not a revocation of a role `roles.json` grants — the server refuses that half and returns
// the file diff to paste into a PR. What it does take back is a grant the Postgres overlay
// made, which is fail-closed in the same way granting it was.
export function revokeRole(
  getToken: TokenGetter,
  body: { email: string },
): Promise<AdminRolesPayload> {
  return request('/api/admin/roles/revoke', getToken, { method: 'POST', body });
}

export function recordSignalToFirstTouch(
  getToken: TokenGetter,
  accountId: string,
): Promise<{ recorded: boolean }> {
  return request('/api/stats/signal-to-first-touch', getToken, {
    method: 'POST',
    body: { account_id: accountId },
  });
}

// The Slack sign-in link. The request id is the whole credential of the prompt, and it rides in
// the path of a GET and the body of the POST — it is single-use and expires in fifteen minutes.
export function fetchSlackLinkRequest(
  getToken: TokenGetter,
  requestId: string,
): Promise<SlackLinkRequest> {
  return request(`/api/slack/link-requests/${encodeURIComponent(requestId)}`, getToken);
}

export function linkSlack(getToken: TokenGetter, requestId: string): Promise<SlackLinkResult> {
  return request('/api/slack/link', getToken, { method: 'POST', body: { request_id: requestId } });
}

// The email style intake (W7), Settings → My style. Each answer is the whole `StyleState`, so the
// page replaces its copy after every step. All writes are POSTs; under impersonation the server
// edits the strategist being acted as and records the admin.
export function fetchStyle(getToken: TokenGetter): Promise<StyleState> {
  return request('/api/style', getToken);
}

export function saveStyleIntake(getToken: TokenGetter, body: StyleIntakeRequest): Promise<StyleState> {
  return request('/api/style/intake', getToken, { method: 'POST', body });
}

export function compileStyle(getToken: TokenGetter, allowGaps: boolean): Promise<StyleState> {
  return request('/api/style/compile', getToken, { method: 'POST', body: { allow_gaps: allowGaps } });
}

export function previewStyle(getToken: TokenGetter, version: number | null): Promise<StyleState> {
  return request('/api/style/preview', getToken, { method: 'POST', body: { version } });
}

export function approveStyle(getToken: TokenGetter, version: number): Promise<StyleState> {
  return request('/api/style/approve', getToken, { method: 'POST', body: { version } });
}

export function editStyleRules(getToken: TokenGetter, body: StyleRulesRequest): Promise<StyleState> {
  return request('/api/style/rules', getToken, { method: 'POST', body });
}

// --- the sequence builder (GRO-287) ---------------------------------------------------------
// The browser names an account, contact ids and step copy; the server resolves every address.

export function fetchSequenceConnection(getToken: TokenGetter): Promise<SequenceConnection> {
  return request('/api/sequences/connection', getToken);
}

export function startSequenceConnection(getToken: TokenGetter): Promise<{ authorize_url: string }> {
  return request('/api/sequences/connection/start', getToken, { method: 'POST', body: {} });
}

export function completeSequenceConnection(
  getToken: TokenGetter, body: { code: string; state: string },
): Promise<SequenceConnection> {
  return request('/api/sequences/connection/callback', getToken, { method: 'POST', body });
}

// Re-reads this person's Amplemarket tools/list and stores the names and the create tools' schemas.
export function refreshSequenceTools(getToken: TokenGetter): Promise<SequenceConnection> {
  return request('/api/sequences/connection/refresh-tools', getToken, { method: 'POST', body: {} });
}

export function disconnectSequenceConnection(getToken: TokenGetter): Promise<{ disconnected: boolean }> {
  return request('/api/sequences/connection/disconnect', getToken, { method: 'POST', body: {} });
}

export function fetchSequenceDrafts(getToken: TokenGetter, accountId: string): Promise<{ drafts: SequenceDraft[] }> {
  return request('/api/sequences/drafts?' + new URLSearchParams({ account_id: accountId }), getToken);
}

export function saveSequenceDraft(
  getToken: TokenGetter,
  body: {
    draft_id: string | null; account_id: string; name: string; campaign_type: string; contact_ids: string[];
    steps: SequenceStep[]; personal_lines: Record<string, string>;
  },
  options: { keepalive?: boolean } = {},
): Promise<{ draft: SequenceDraft }> {
  return request('/api/sequences/drafts/save', getToken, { method: 'POST', body, ...options });
}

// One step, written once for everyone; the server drafts against the first picked person.
export function draftSequenceStep(
  getToken: TokenGetter,
  body: { account_id: string; contact_ids: string[]; steps: SequenceStep[]; step_index: number },
): Promise<SequenceStepDraft> {
  return request('/api/sequences/drafts/draft-step', getToken, { method: 'POST', body });
}

// One person's {{personal_line}}: the opener Scout would write them for the first email.
export function draftSequencePersonalLine(
  getToken: TokenGetter,
  body: { account_id: string; contact_id: string; steps: SequenceStep[] },
): Promise<SequencePersonalLineDraft> {
  return request('/api/sequences/drafts/personal-line', getToken, { method: 'POST', body });
}

export function fetchSequenceMailboxes(getToken: TokenGetter): Promise<SequenceMailboxes> {
  return request('/api/sequences/connection/mailboxes', getToken);
}

// `existing_sequence_id` / `confirm_new_create` settle a create whose outcome is unknown;
// `mailbox` is the one it sends from (empty: the strategist's own).
export function createSequenceInAmplemarket(
  getToken: TokenGetter, draftId: string,
  body: { existing_sequence_id?: string; confirm_new_create?: boolean; mailbox?: string } = {},
): Promise<SequenceCreateReply> {
  return request(`/api/sequences/drafts/${encodeURIComponent(draftId)}/create`, getToken, { method: 'POST', body });
}

// The one call that starts sending. Only the confirmed "Create and launch" click makes it.
export function launchSequenceInAmplemarket(
  getToken: TokenGetter, draftId: string, confirmedLeadCount: number,
): Promise<SequenceLaunchReply> {
  return request(`/api/sequences/drafts/${encodeURIComponent(draftId)}/launch`, getToken, {
    method: 'POST', body: { confirmed_lead_count: confirmedLeadCount },
  });
}

// `/admin/retire`: the week's retirement proposal. Admin only; the write is a POST bound to the real actor.
export function fetchRetireProposal(getToken: TokenGetter): Promise<RetirePayload> {
  return request<RetirePayload>('/api/admin/retire', getToken);
}

export function applyRetireProposal(
  getToken: TokenGetter,
  proposalKey: string,
  accountIds: string[],
  fingerprint: string | null,
): Promise<RetireApplyPayload> {
  return request('/api/admin/retire/apply', getToken, {
    method: 'POST', body: { proposal_key: proposalKey, account_ids: accountIds, fingerprint: fingerprint ?? undefined },
  });
}
